import axios, {
  AxiosRequestConfig,
  AxiosRequestHeaders,
  isAxiosError,
} from 'axios';

import AuthService from '@auth/AuthService';
import APP_VERSION from '@constants/AppVersion';

import {
  getResolvedDrumkitApiBaseUrl,
  isDrumkitApiBaseRoutingActive,
  syncVestaAxiosApiBaseUrlFromSession,
} from './drumkitHostResolution';
import { getMailboxEmailAddress } from './outlookMailbox';

export interface AxiosRequestConfigExtended extends AxiosRequestConfig {
  _retry?: boolean;
  _bypassInterceptor?: boolean;
}

const instance = axios.create({
  baseURL: (() => {
    if (!isDrumkitApiBaseRoutingActive()) {
      return '/api';
    }
    return getResolvedDrumkitApiBaseUrl(getMailboxEmailAddress(), null);
  })(),
  headers: {
    'Content-Type': 'application/json;charset=UTF-8',
  },
});

instance.interceptors.request.use(async (config) => {
  if (isDrumkitApiBaseRoutingActive()) {
    const roaming = await AuthService.getCurrentUser();
    config.baseURL = syncVestaAxiosApiBaseUrlFromSession(
      instance,
      roaming?.email ?? null,
      roaming?.api_base_url_at_issue
    );
  }

  const authHeader = await AuthService.getAuthHeader();
  config.headers['Vesta-Version'] = APP_VERSION;

  config.headers = {
    ...config.headers,
    ...authHeader,
  } as AxiosRequestHeaders;
  return config;
});

// Portal isolation returns this when the email resolves to a different
// cluster than the API that received the request. Clear the pinned
// session so the next open shows Sign in instead of a stuck JWT.
export function isWrongPortalError(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 403) {
    return false;
  }
  const data = error.response.data;
  if (!data || typeof data !== 'object') {
    return false;
  }
  return (data as { error?: unknown }).error === 'wrong_portal';
}

instance.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (isWrongPortalError(error)) {
      await AuthService.logOut();
    }
    return Promise.reject(error);
  }
);

export default instance;
